/**
 * Trajectory scoring: simulate one aim and grade where it lands.
 * scoreFlightOutcome is mirrored in wasm-physics/src/lib.rs score_trajectory —
 * keep the two in sync (tests/wasmParity.test.ts checks).
 */

import type { PlayerState, PlanetState, Vec3, WeaponKey } from '../../types';
import type { FlightOutcome } from './types';
import { vecLength, vecSub, vecNormalize, vecDot, vecScale, vecAdd } from '../../math';
import { WEAPONS, PHYS_DT, OUT_OF_BOUNDS, G, MAX_PROJECTILE_TIME } from '../../constants';

/** The real flight's length in steps (60 s). */
export const FULL_FLIGHT_STEPS = Math.round(MAX_PROJECTILE_TIME / PHYS_DT);
/** Escape test cadence and launch grace, in steps; margin above a body's radius before "leaving" counts. Mirrored in lib.rs. */
export const ESCAPE_TEST_EVERY = 30;
export const ESCAPE_TEST_FROM = 60;
export const ESCAPE_MARGIN = 20;

/** Where the search's flights go (JS scorer only; the kernel does not report steps). Dev measurement, reset by the caller. */
export const searchStats = { flights: 0, steps: 0, hit: 0, otherPlanet: 0, oob: 0, escaped: 0, capped: 0, ownGraze: 0 };
export function resetSearchStats(): void { for (const k of Object.keys(searchStats) as Array<keyof typeof searchStats>) searchStats[k] = 0; }

/**
 * A flight that is beyond every body by a margin, moving away from each, with more kinetic energy than
 * the potential binding it is unbound: it will drift out of bounds. Summation in planet order — the
 * kernel does the same arithmetic.
 */
export function isEscaping(pos: Vec3, vel: Vec3, planets: PlanetState[]): boolean {
  let potential = 0;
  for (const p of planets) {
    if (!p.alive) continue;
    const dx = pos.x - p.position.x, dy = pos.y - p.position.y, dz = pos.z - p.position.z;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (d < p.radius + ESCAPE_MARGIN) return false;
    if (dx * vel.x + dy * vel.y + dz * vel.z <= 0) return false;
    potential += G * p.mass / d;
  }
  const kinetic = 0.5 * (vel.x * vel.x + vel.y * vel.y + vel.z * vel.z);
  return kinetic > potential;
}
import { computeLaunchDirection } from '../../core/gameState';
import { rk4Step, checkCollision, megaPlanetPowerMult } from '../../core/physics';
import { getSurfaceRadius } from '../../core/surface';
import { dacos } from '../../core/dmath';

/**
 * Score a trajectory: how close does it get to the target?
 * Returns miss distance (lower = better). Negative means damaging hit.
 *
 * Uses the same PHYS_DT timestep as the real game to avoid simulation
 * divergence on complex orbital trajectories.
 */
export function scoreTrajectory(
  player: PlayerState,
  planets: PlanetState[],
  targetPos: Vec3,
  targetPlanetIndex: number,
  azimuth: number,
  elevation: number,
  power: number,
  weaponKey: WeaponKey,
  allPlayers?: PlayerState[],
  maxSteps: number = FULL_FLIGHT_STEPS,
  escapeTest = true,
): number {
  const planet = planets[player.planetIndex];
  const surfaceNormal = vecNormalize(player.surfaceDir);
  const surfaceRadius = getSurfaceRadius(planet, player.surfaceDir);
  const worldPos = vecAdd(planet.position, vecScale(surfaceNormal, surfaceRadius));
  const startPos = vecAdd(worldPos, vecScale(surfaceNormal, 1.2));

  const launchDir = computeLaunchDirection(surfaceNormal, azimuth, elevation);
  const speed = power * megaPlanetPowerMult(planet.radius);
  const weapon = WEAPONS[weaponKey];
  const effectiveRadius = weapon.blastRadius * 2.2;

  let pos = { ...startPos };
  let vel = vecScale(launchDir, speed);

  let collisionPlanetIndex = -1;
  let impactPos: Vec3 | null = null;
  let impactVel: Vec3 | null = null;
  const ownPlanetRadius = planet.radius + 2; // small margin for heightmap variation
  const targetPlanet = planets[targetPlanetIndex];

  // Track closest approach to the TARGET PLANET SURFACE (not the player).
  let closestSurfaceDist = Infinity;
  searchStats.flights++;
  let i = 0;

  for (; i < maxSteps; i++) {
    const result = rk4Step(pos, vel, PHYS_DT, planets);
    pos = result.pos;
    vel = result.vel;

    // Track closest approach to target planet surface (every 3rd step)
    if (i % 3 === 0) {
      const distToCenter = vecLength(vecSub(pos, targetPlanet.position));
      const distToSurface = distToCenter - targetPlanet.radius;
      if (distToSurface < closestSurfaceDist) closestSurfaceDist = distToSurface;
    }

    // Check collision
    const collision = checkCollision(pos, planets);
    if (collision) {
      collisionPlanetIndex = collision.planetIndex;
      impactPos = collision.point; // surface-projected, same as the WASM scorer (raw pos was ~0.05 off)
      impactVel = { ...vel };
      break;
    }

    // OOB check
    if (vecLength(pos) > OUT_OF_BOUNDS) { searchStats.oob++; break; }

    // Near own planet safety check (after launch phase)
    if (i > 20 && targetPlanetIndex !== player.planetIndex) {
      const distToOwn = vecLength(vecSub(pos, planet.position));
      if (distToOwn < ownPlanetRadius) {
        searchStats.steps += i + 1; searchStats.ownGraze++;
        return Infinity; // trajectory grazes own planet — banned
      }
    }

    // Escape test: unbound and leaving every body — it will be lost; stop flying it
    if (escapeTest && i >= ESCAPE_TEST_FROM && i % ESCAPE_TEST_EVERY === 0 && isEscaping(pos, vel, planets)) { searchStats.escaped++; break; }
  }
  searchStats.steps += Math.min(i + 1, maxSteps);
  if (collisionPlanetIndex === targetPlanetIndex) searchStats.hit++;
  else if (collisionPlanetIndex >= 0) searchStats.otherPlanet++;
  else if (i >= maxSteps) searchStats.capped++;

  return scoreFlightOutcome({
    collisionPlanetIndex, impactPos, impactVel, closestSurfaceDist,
    shooterPlanetIndex: player.planetIndex,
    shooterPos: worldPos,
    targetPlanetIndex, targetPlanetPos: targetPlanet.position, targetPlanetRadius: targetPlanet.radius,
    targetPos, effectiveRadius,
    teammatePlanetIndices: allPlayers
      ? allPlayers.filter(p => p.id !== player.id && p.alive && p.teamIndex === player.teamIndex).map(p => p.planetIndex)
      : [],
  });
}

/**
 * Score a simulated flight outcome (lower = better). Pure; mirrored in
 * wasm-physics/src/lib.rs score_trajectory — keep in sync.
 *
 *   negative     direct hit on the target planet inside the damage radius
 *   0 .. ~50     hit target planet outside the radius (arc-distance gradient)
 *   50 .. 100    near miss of the target planet
 *   200+         wrong planet
 *   500+         out of bounds / timeout
 *   Infinity     banned (own planet, teammate planet, hurts us more than them)
 */
export function scoreFlightOutcome(o: FlightOutcome): number {
  if (o.collisionPlanetIndex === o.targetPlanetIndex && o.impactPos) {
    // Euclidean distance for the damage check (matches the damage model)
    const impactDist = vecLength(vecSub(o.impactPos, o.targetPos));

    // Arc distance on the planet surface gives a smooth gradient around the whole planet
    const impactDir = vecNormalize(vecSub(o.impactPos, o.targetPlanetPos));
    const targetDir = vecNormalize(vecSub(o.targetPos, o.targetPlanetPos));
    const angularDist = dacos(Math.max(-1, Math.min(1, vecDot(impactDir, targetDir))));
    const arcDist = angularDist * o.targetPlanetRadius;

    // How perpendicular the impact is (1 = straight down, 0 = grazing)
    let perpendicularity = 1;
    if (o.impactVel) {
      perpendicularity = Math.max(0, -vecDot(vecNormalize(o.impactVel), impactDir));
    }

    // Self-blast: a same-planet shot landing inside our own damage radius
    let selfPenalty = 0;
    if (o.targetPlanetIndex === o.shooterPlanetIndex) {
      const selfDist = vecLength(vecSub(o.impactPos, o.shooterPos));
      if (selfDist <= o.effectiveRadius) {
        if (selfDist < impactDist) return Infinity; // hurts us more than them — never
        selfPenalty = 40 * (1 - selfDist / o.effectiveRadius);
      }
    }

    if (impactDist <= o.effectiveRadius) {
      // Damage decides; steepness is only a small tiebreaker. (The old
      // perpendicularity² multiplier made an 80%-radius steep hit outscore a
      // centre-grazing hit, even though damage ignores impact angle.)
      const damageFactor = 1 - impactDist / o.effectiveRadius;
      return -100 * damageFactor - 3 * perpendicularity + selfPenalty;
    }
    // Outside the radius: arc gradient, grazing approaches mildly penalised
    return arcDist * (1.5 - 0.5 * perpendicularity) + selfPenalty;
  }

  if (o.collisionPlanetIndex >= 0) {
    if (o.collisionPlanetIndex === o.shooterPlanetIndex && o.targetPlanetIndex !== o.shooterPlanetIndex) {
      return Infinity; // never hit our own planet unless the enemy is on it
    }
    if (o.collisionPlanetIndex !== o.targetPlanetIndex && o.teammatePlanetIndices.includes(o.collisionPlanetIndex)) {
      return Infinity; // never hit a planet a teammate is standing on
    }
    return 200 + o.closestSurfaceDist; // wrong planet
  }

  // Near miss: very promising, a tiny parameter change likely converts it to a hit
  if (o.closestSurfaceDist < 5) {
    return 50 + o.closestSurfaceDist;
  }

  // OOB / timeout
  return 500 + o.closestSurfaceDist;
}
